/**
 * The หมอนวด and เตียง pickers.
 *
 * Shared by the จองเลย builder and by จัดหมอนวดและเตียง on เว็บจอง, because the
 * two screens are answering the same question — who is free and which bed will
 * do — and a party seated from a booking request has to obey exactly the rules
 * a walk-in does. Two copies would be two sets of rules within a week.
 */

/** เข้างาน / ยังไม่เข้างาน, as a numbered list of rows. */
export function TherapistPicker({
  lanes,
  all,
  availability = [],
  blockedIds = [],
  requireOnDuty = false,
  onPick,
  gender,
  onGender,
  selectedIds = [],
}) {
  // `lanes` comes off the board keyed by therapistId and from the roster keyed
  // by id, so the on-duty split has to read either.
  const idOf = (t) => String(t.id ?? t.therapistId);
  const onDutyIds = new Set(lanes.map(idOf));
  const off = all.filter((t) => !onDutyIds.has(idOf(t)));
  const availabilityById = new Map(availability.map((t) => [idOf(t), t]));
  const blocked = new Set(blockedIds.map(String));
  const selected = new Set(selectedIds.map(String));
  const name = (t) => `${t.nicknameEn || t.nickname_en || ''}${
    (t.nicknameTh || t.nickname_th) ? ` (${t.nicknameTh || t.nickname_th})` : ''}`.trim();
  const genderOf = (t) => t.gender || t.genderType || '';

  const Group = ({ title, items, onDuty }) => {
    const shown = gender ? items.filter((t) => genderOf(t) === gender) : items;
    return (
      <>
        <div className="menu-group ruled">{title}</div>
        {shown.length === 0 ? (
          <div className="group-none">ไม่มีรายชื่อ</div>
        ) : shown.map((t, i) => {
          const id = idOf(t);
          const status = availabilityById.get(id);
          const unavailable = blocked.has(id)
            || status?.isFree === false
            || (requireOnDuty && !onDuty);
          const reason = blocked.has(id) || status?.isFree === false
            ? 'ไม่ว่างในเวลานี้'
            : requireOnDuty && !onDuty ? 'ยังไม่เข้างาน' : '';
          return (
            <button
              key={id}
              type="button"
              disabled={unavailable}
              className={`pick-row${onDuty ? '' : ' off'}${
                unavailable ? ' unavailable' : ''}${selected.has(id) ? ' on' : ''}`}
              onClick={() => onPick(t, onDuty)}
              title={reason}
            >
              <span className="pick-no">{i + 1}.</span>
              <span className={`pick-avatar${onDuty ? ' on-duty' : ''}`} />
              <span className="pick-row-name">{name(t) || t.code}</span>
              {reason && <span className="pick-unavailable">{reason}</span>}
            </button>
          );
        })}
      </>
    );
  };

  return (
    <>
      {/* the ♀ ♂ ⚧ filter sits beside the category dropdown on the real panel */}
      <div className="pick-genders">
        {[['female', '♀', 'g-female'], ['male', '♂', 'g-male'], ['other', '⚧', 'g-other']]
          .map(([v, glyph, cls]) => (
            <button
              key={v}
              className={`gender-btn ${cls}${gender === v ? ' on' : ''}`}
              onClick={() => onGender(gender === v ? '' : v)}
            >
              {glyph}
            </button>
          ))}
      </div>
      <Group title="เข้างาน" items={lanes} onDuty />
      <Group title="ยังไม่เข้างาน" items={off} />
    </>
  );
}

/** รองรับบริการนี้ / ไม่รองรับบริการนี้, grouped by room. */
export function BedPicker({ beds, onPick, selectedIds = [], blockedIds = [] }) {
  const byRoom = (list) => list.reduce((acc, b) => {
    const k = b.roomNameTh || b.roomCode || 'ไม่ระบุห้อง';
    (acc[k] ||= []).push(b);
    return acc;
  }, {});

  const selected = new Set(selectedIds.map(String));
  const blocked = new Set(blockedIds.map(String));
  const ok = beds.filter((b) =>
    b.supportsService && b.isFree !== false && !blocked.has(String(b.id))
  );
  const busy = beds.filter((b) =>
    b.supportsService && (b.isFree === false || blocked.has(String(b.id)))
  );
  const no = beds.filter((b) => !b.supportsService);

  const Group = ({ title, list, muted, reason }) => (
    <>
      <div className="menu-group ruled">{title}</div>
      {list.length === 0 ? (
        <div className="group-none">ไม่มีรายชื่อ</div>
      ) : Object.entries(byRoom(list)).map(([room, items]) => (
        <div key={room} className="bed-room">
          <div className="bed-room-name">{room}</div>
          <div className="pick-grid">
            {items.map((b) => (
              <button
                key={b.id}
                type="button"
                disabled={muted}
                className={`pick-tile${muted ? ' off unavailable' : ''}${
                  selected.has(String(b.id)) ? ' on' : ''}`}
                onClick={() => onPick(b)}
                title={reason || b.bedTypeName || ''}
              >
                <span className="pick-bed">{b.code}</span>
                {reason && <span className="pick-unavailable">{reason}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <>
      <Group title="รองรับบริการนี้" list={ok} />
      <Group title="ไม่ว่างในเวลานี้" list={busy} muted reason="ไม่ว่าง" />
      <Group title="ไม่รองรับบริการนี้" list={no} muted reason="ใช้กับบริการนี้ไม่ได้" />
    </>
  );
}
